import React from 'react';
import { useAssessment } from '../context/AssessmentContext';
import {
  functionalRoles,
  companySizeOptions,
  businessLifecycleOptions,
  technologyAdoptionOptions,
  interestedAreasOptions
} from '../data/assessmentData';

function AboutYouSection() {
  const { state, dispatch } = useAssessment();

  const handleChange = (field, value) => {
    dispatch({
      type: 'SET_ABOUT_YOU',
      payload: { [field]: value }
    });
  };

  const handleInterestToggle = (area) => {
    const current = state.aboutYou.interestedAreas || [];
    const updated = current.includes(area)
      ? current.filter(a => a !== area)
      : [...current, area];
    handleChange('interestedAreas', updated);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-800">About You & Your Organization</h2>
        <p className="text-gray-600">Please provide a bit more context to help us tailor your report.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Company Name - Added Field */}
        {!state.userInfo.userProvidedCode && (
          <div className="md:col-span-2">
            <label className="block text-sm font-bold text-gray-700 mb-1">What is the name of your company?</label>
            <input
              type="text"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              value={state.aboutYou.companyName}
              onChange={(e) => handleChange('companyName', e.target.value)}
              placeholder="e.g., Acme Corp"
            />
          </div>
        )}

        {/* Role */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Your Role</label>
          <select
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            value={state.userInfo.functionalRole}
            onChange={(e) => dispatch({ type: 'SET_USER_INFO', payload: { functionalRole: e.target.value } })}
          >
            <option value="">Select...</option>
            {functionalRoles.map(role => <option key={role} value={role}>{role}</option>)}
          </select>
        </div>

        {/* Company Size */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Company Size (Employees)</label>
          <select
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            value={state.aboutYou.companySize}
            onChange={(e) => handleChange('companySize', e.target.value)}
          >
            <option value="">Select...</option>
            {companySizeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>

        {/* Lifecycle */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Business Lifecycle Stage</label>
          <select
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            value={state.aboutYou.businessLifecycle}
            onChange={(e) => handleChange('businessLifecycle', e.target.value)}
          >
            <option value="">Select...</option>
            {businessLifecycleOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>

        {/* Technology Adoption */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Technology Adoption Style</label>
          <select
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            value={state.aboutYou.technologyAdoption}
            onChange={(e) => handleChange('technologyAdoption', e.target.value)}
          >
            <option value="">Select...</option>
            {technologyAdoptionOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
      </div>

      {/* Text Areas */}
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">What products or services do you offer?</label>
          <textarea
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            rows="2"
            value={state.aboutYou.productServices}
            onChange={(e) => handleChange('productServices', e.target.value)}
            placeholder="Briefly describe what you sell or provide..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Who is your ideal client?</label>
          <textarea
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            rows="2"
            value={state.aboutYou.idealClient}
            onChange={(e) => handleChange('idealClient', e.target.value)}
            placeholder="Describe your target audience..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">What do you enjoy most about your business?</label>
          <textarea
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            rows="2"
            value={state.aboutYou.favoriteElements}
            onChange={(e) => handleChange('favoriteElements', e.target.value)}
            placeholder="What aspects give you the most energy?"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">What are the biggest obstacles to your success?</label>
          <textarea
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            rows="2"
            value={state.aboutYou.biggestObstacles}
            onChange={(e) => handleChange('biggestObstacles', e.target.value)}
            placeholder="What's holding you back right now?"
          />
        </div>
      </div>

      {/* Motivation Slider */}
      <div className="bg-blue-50 p-6 rounded-xl border border-blue-100">
        <label className="block text-sm font-bold text-gray-800 mb-4 text-center">
          How motivated are you to make changes in your organization? (1-10)
        </label>
        <div className="flex items-center gap-4">
          <span className="text-gray-500 font-bold text-lg">1</span>
          <input
            type="range"
            min="1"
            max="10"
            step="1"
            className="w-full h-3 bg-gray-300 rounded-lg appearance-none cursor-pointer accent-blue-600"
            value={state.aboutYou.motivationLevel}
            onChange={(e) => handleChange('motivationLevel', parseInt(e.target.value))}
          />
          <span className="text-gray-500 font-bold text-lg">10</span>
        </div>
        <div className="text-center mt-3">
          <span className="inline-block px-4 py-1 bg-blue-600 text-white font-bold rounded-full">
            {state.aboutYou.motivationLevel}
          </span>
        </div>
      </div>

      {/* Interested Areas */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Which areas are you most interested in improving? (Select all that apply)
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {interestedAreasOptions.map(area => (
            <label
              key={area}
              className={`
                flex items-center justify-center p-3 rounded-lg border cursor-pointer transition-all text-sm font-medium text-center select-none shadow-sm
                ${(state.aboutYou.interestedAreas || []).includes(area)
                  ? 'bg-blue-600 border-blue-600 text-white shadow-md transform scale-[1.02]'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'}
              `}
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={(state.aboutYou.interestedAreas || []).includes(area)}
                onChange={() => handleInterestToggle(area)}
              />
              {area}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

export default AboutYouSection;